import React, { useMemo, useState, useEffect, useRef, useContext } from "react";
import { GoogleMap,MarkerF, OverlayViewF, OVERLAY_MOUSE_TARGET, useLoadScript } from "@react-google-maps/api";
import { SearchContext } from "../../context/SearchContext";
import config from "../../config";

const containerStyle = {
  width: "100%",
  height: "500px", // adjust height as needed
  borderRadius: "12px",
};

const FALLBACK_CENTER = { lat: 28.5355, lng: 77.3910 };

export default function VendorMap({ vendors: propVendors = [] ,
   hoveredVendorId = null,               
  setHoveredVendorId = () => {}, 
   singleView, defaultZoom }) 
   {

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.GOOGLE_MAPS_KEY || process.env.REACT_APP_GOOGLE_MAPS_KEY,
  });

  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const mapRef = useRef(null);
  const { vendors: ctxVendors } = useContext(SearchContext);

  const [hoveredMarkerId, setHoveredMarkerId] = useState(null);
  const activeHoverId = hoveredVendorId || hoveredMarkerId;
  const activeVendorId = hoveredVendorId || hoveredMarkerId;
  const [hasInitialized, setHasInitialized] = useState(false);
  
 const [mapCenter, setMapCenter] = useState(FALLBACK_CENTER);
  const BASE_Y_OFFSET_PX = -60;
  const HOVER_Y_OFFSET_PX = -61; // small lift on hover

  const getVendorId = (v) => {
  const raw = v.vendor_id || v._id;

  if (!raw) return null;

  if (typeof raw === "string") return raw;

  if (raw.$oid) return raw.$oid;

  if (raw._id) return raw._id;

  return String(raw);
};
  // Normalize incoming vendor collections to a flat array
  const normalizeVendors = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) {
      if (data.length === 1 && Array.isArray(data[0]?.data)) return data[0].data;
      if (data.every((v) => v && (v._id || v.vendor_id))) return data;
    }
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.vendors)) return data.vendors;
    return Array.isArray(data) ? data : [];
  };

  // Compute effective vendors synchronously to avoid timing issues on refresh
  const effectiveVendors = useMemo(() => {
    if (propVendors && propVendors.length) return normalizeVendors(propVendors);
    if (ctxVendors && ctxVendors.length) return normalizeVendors(ctxVendors);
    try {
      const saved = localStorage.getItem("vendors");
      if (saved) return normalizeVendors(JSON.parse(saved));
    } catch (e) {
      // ignore parse errors
    }
    return vendors && vendors.length ? normalizeVendors(vendors) : [];
  }, [propVendors, ctxVendors, vendors]);

  // Prefer vendors from props; then context; fallback to localStorage
  useEffect(() => {
    if (propVendors && Array.isArray(propVendors) && propVendors.length > 0) {
      setVendors(propVendors);
      return;
    }
    if (ctxVendors && Array.isArray(ctxVendors) && ctxVendors.length > 0) {
      setVendors(ctxVendors);
      return;
    }
    const savedVendors = localStorage.getItem("vendors");
    if (savedVendors) {
      try {
        const parsed = JSON.parse(savedVendors);
        setVendors(parsed);
      } catch (e) {
        console.error("Error parsing vendors from localStorage", e);
      }
    }
  }, [propVendors, ctxVendors]);

  // Extract lat/lng (handles strings or numbers)
  const extractLatLng = (v) => {
    let lat, lng;
    if (v?.address?.officeAddress?.latitude && v?.address?.officeAddress?.longitude) {
      lat = parseFloat(v.address.officeAddress.latitude);
      lng = parseFloat(v.address.officeAddress.longitude);
    } else if (v?.address?.homeAddress?.latitude && v?.address?.homeAddress?.longitude) {
      lat = parseFloat(v.address.homeAddress.latitude);
      lng = parseFloat(v.address.homeAddress.longitude);
    } else if (v?.address?.officeAddress?.location?.coordinates && Array.isArray(v.address.officeAddress.location.coordinates)) {
      const [lon, la] = v.address.officeAddress.location.coordinates;
      lat = parseFloat(la);
      lng = parseFloat(lon);
    } else if (v?.address?.homeAddress?.location?.coordinates && Array.isArray(v.address.homeAddress.location.coordinates)) {
      const [lon, la] = v.address.homeAddress.location.coordinates;
      lat = parseFloat(la);
      lng = parseFloat(lon);
    } else if (v.lat && v.long) {
      lat = parseFloat(v.lat);
      lng = parseFloat(v.long);
    } else if (v.lat && v.lng) {
      lat = parseFloat(v.lat);
      lng = parseFloat(v.lng);
    } else {
      console.error("No valid coordinates for vendor", v);
      return null;
    }
    if (isNaN(lat) || isNaN(lng)) return null;
    // New: Skip obvious invalid/default (0,0) or out-of-range for India
      if (Math.abs(lat) < 1e-6 && Math.abs(lng) < 1e-6) {
        return null;
      }
      if (lat < 8 || lat > 37 || lng < 68 || lng > 97) {
        return null;
      }
    return { lat, lng };
  };

  const vendorMarkers = useMemo(() => {
    return effectiveVendors
      .map((v) => {
        const ll = extractLatLng(v);
        if (!ll) return null;
        const name = v.profile?.brand || v.profile?.companyName;
        const id = getVendorId(v);
      if (!id) return null;
        console.log(`Vendor ${id} (${name}): lat=${ll.lat}, lng=${ll.lng}`);
        return {
         // id: v.vendor_id?.$oid || v._id?.$oid || Math.random().toString(36).slice(2, 7),
          id,
         name,
          position: ll,
        };
      })
      .filter(Boolean);
  }, [effectiveVendors]);

    console.log("Hovered:", hoveredVendorId);
    console.log("Markers:", vendorMarkers.map(v => v.id));

    const handleMapLoad = (map) => {
      mapRef.current = map;
    };

    useEffect(() => {
      if (!mapRef.current || vendorMarkers.length === 0) return;
      if (hoveredVendorId) return; // don't fight hover
      
      if (vendorMarkers.length === 1) {
        mapRef.current.setCenter(vendorMarkers[0].position);
        mapRef.current.setZoom(defaultZoom || 14);
        setHasInitialized(true);
        return;
      }
      const bounds = new window.google.maps.LatLngBounds();
      vendorMarkers.forEach(m => bounds.extend(m.position));
      mapRef.current.fitBounds(bounds); 
      setHasInitialized(true); 
      
      setTimeout(() => {
        if (mapRef.current) {
          const currentZoom = mapRef.current.getZoom();
          console.log("Zoom after fitBounds:", currentZoom); 

          if (currentZoom < 11) {          
            mapRef.current.setZoom(12);    
          } else if (currentZoom > 17) { 
            mapRef.current.setZoom(15);
          }
        }
      }, 200); 
    }, [vendorMarkers,hoveredVendorId, defaultZoom]);

    console.log(
      "Vendor markers data:",
      vendorMarkers.map(m => ({
        id: m.id,
        hasPosition: !!m.position,
        name: m.name,
      }))
    );
  console.log("selectedVendor changed to:", selectedVendor?.id || "null");


  useEffect(() => {
    if (hoveredVendorId) {
      const hoveredVendor = vendorMarkers.find(v => v.id === hoveredVendorId);
      if (hoveredVendor) {
        setSelectedVendor(hoveredVendor);
        // Optional: slight zoom for emphasis
      //  mapRef.current?.setZoom(15);
      }
    } else {
      setSelectedVendor(null);
    }
  }, [hoveredVendorId, vendorMarkers]);

  useEffect(() => {
  if (vendorMarkers.length > 0) {
    setMapCenter(vendorMarkers[0].position);
  }
}, []);

  if (loadError) return <div>Error loading map.</div>;
  if (!isLoaded) return <div>Loading map...</div>;

  const isSingleVendor = vendorMarkers.length === 1;

  const initialCenter = isSingleVendor
  ? vendorMarkers[0].position
  : FALLBACK_CENTER;

   const initialZoom = isSingleVendor
    ? defaultZoom || 14 
    : 12; 

     const VendorMarker = ({ name, active }) => (
      <div
        style={{
          padding: "6px 14px",
          backgroundColor: active ? "#1f4fff" : "#ffffff",
          color: active ? "#ffffff" : "#222",
          borderRadius: "18px",
          fontSize: "12px",
          fontWeight: 600,
          boxShadow: active
            ? "0 10px 24px rgba(0,0,0,0.35)"
            : "0 2px 8px rgba(0,0,0,0.25)",

          /* 🔥 THIS IS THE KEY */
          transform: active
            ? "translateY(-14px) scale(1.12)"
            : "translateY(0) scale(1)",

          transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
          whiteSpace: "nowrap",
          pointerEvents: "auto",
        }}
      >
        {name}
      </div>
    );

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={mapCenter}
      zoom={initialZoom}
      onLoad={handleMapLoad}
    >
    {vendorMarkers.map((vendor) => {
      const active = hoveredVendorId === vendor.id;

  return (
    <React.Fragment key={vendor.id}>

      {/* 🔴 RED GOOGLE MARKER */}
      <MarkerF
        position={vendor.position}
        zIndex={active ? 10 : 1}
        onMouseOver={() => setHoveredVendorId(vendor.id)}
        onMouseOut={() => setHoveredVendorId(null)}
      />

      {/* 🏷️ LABEL */}
      <OverlayViewF
        position={vendor.position}
        mapPaneName={OVERLAY_MOUSE_TARGET}
      >
        <div
          style={{
            transform: `translate(-50%, ${active ? HOVER_Y_OFFSET_PX : BASE_Y_OFFSET_PX}px)`,
            transition: "transform 0.2s ease",
            pointerEvents: "auto",
            zIndex: active ? 999 : 1,
          }}
          onMouseEnter={() => setHoveredVendorId(vendor.id)}
          onMouseLeave={() => setHoveredVendorId(null)}
        >
          <VendorMarker name={vendor.name} active={active} />
        </div>
      </OverlayViewF>

    </React.Fragment>
  );
})}
    </GoogleMap>
  );
  
}
